import React from 'react';
import {Skeleton, view} from '@sail/ui';
import {message, useIntl} from '@sail/intl';
import {Text} from 'src/v2/components/common';
import {AssetWithNetworkIcon} from 'src/v2/components/common/AssetWithNetworkIcon';
import {getCurrencyIcon} from 'src/v2/util/getCurrencyIcon';
import {formatAssetCode} from 'src/v2/util/formatAssetCode';
import {
  getNetworkIcon,
  getNetworkNameForLivemode,
} from 'src/components/Wallet/constants';
import {useSelector} from 'src/v2/state/store';
import {CountdownRing} from 'src/v2/components/common/CountdownRing';
import type {SupportedNetwork} from 'src/types';
import type {SupportedCurrency} from 'src/v2/state/types';

const messages = {
  youSendAmount: message<{
    amount: string;
    asset: string;
    network: string;
    label: (chunks: React.ReactNode) => React.ReactNode;
    value: (chunks: React.ReactNode) => React.ReactNode;
  }>({
    id: 'crypto.payins.v2.token_card.you_send_amount',
    description:
      'Summary of what the customer will send: a "You send" label above the crypto amount and network, e.g. "You send" then "0.00062 ETH on Base".',
    defaultMessage:
      '<label>You send</label><value>{amount} {asset} on {network}</value>',
  }),
  refreshCountdown: message<{seconds: number}>({
    id: 'crypto.payins.v2.token_card.refresh_countdown',
    description: 'Countdown timer for quote refresh',
    defaultMessage: 'Refresh in {seconds}s',
  }),
};

type RefreshCountdownInfo =
  | {
      secondsUntilRefresh: number;
      totalSeconds: number;
    }
  | {loading: true};

const RefreshCountdown: React.FC<RefreshCountdownInfo> = (props) => {
  const intl = useIntl();

  if ('loading' in props) {
    return (
      <Skeleton
        css={{width: 'space.1000', height: 'space.150', paddingTop: 'small'}}
      />
    );
  }

  const {secondsUntilRefresh, totalSeconds} = props;

  return (
    <view.div css={{stack: 'x', gap: 'small', alignY: 'center'}}>
      <CountdownRing
        seconds={secondsUntilRefresh}
        total={totalSeconds}
        size={10}
        strokeWidth={1.5}
      />
      <Text type="caption" css={{color: 'subdued'}}>
        {intl.formatMessage(messages.refreshCountdown, {
          seconds: secondsUntilRefresh,
        })}
      </Text>
    </view.div>
  );
};

export interface TokenCardProps {
  /** The crypto asset being sent, e.g. `eth`. */
  assetCode: SupportedCurrency['assetCode'];
  /** The network the asset is sent on, e.g. `base`. */
  network: SupportedNetwork;
  /** The formatted amount to send, e.g. `0.00062`. */
  amount: string;
  refreshCountdown: RefreshCountdownInfo | null;
  /** When true, show a skeleton in place of the amount while the quote loads. */
  loading?: boolean;
}

export const TokenCard: React.FC<TokenCardProps> = ({
  assetCode,
  network,
  amount,
  refreshCountdown,
  loading,
}) => {
  const intl = useIntl();
  const livemode = useSelector((state) => state.livemode);

  return (
    <view.div
      css={{
        stack: 'x',
        gapX: 'space.150',
        alignY: 'top',
        borderRadius: 'small',
        borderColor: 'border',
        borderWidth: 'small',
        borderStyle: 'solid',
        padding: 'medium',
      }}
    >
      <AssetWithNetworkIcon
        assetIcon={getCurrencyIcon(assetCode)}
        networkIcon={getNetworkIcon(network)}
      />
      <view.div css={{stack: 'y', gap: 'xsmall'}}>
        {intl.formatMessage(messages.youSendAmount, {
          amount,
          asset: formatAssetCode(assetCode),
          network: getNetworkNameForLivemode(network, livemode),
          label: (chunks) => (
            <Text type="body" css={{color: 'subdued'}}>
              {chunks}
            </Text>
          ),
          value: (chunks) =>
            loading ? (
              <Skeleton css={{width: '160px', height: 'space.250'}} />
            ) : (
              <Text type="mediumEmphasized">{chunks}</Text>
            ),
        })}
        {refreshCountdown ? (
          <RefreshCountdown
            {...(loading ? {loading: true} : refreshCountdown)}
          />
        ) : null}
      </view.div>
    </view.div>
  );
};
